import * as _ from "lodash-es";
import React, { ChangeEvent, Component } from "react";
import { DragDropContext, Draggable, DraggableLocation, Droppable, DropResult, ResponderProvided } from "@hello-pangea/dnd";
import IOrder from "../../../../models/IOrder";
import IOrder_Progress_State from "../../../../models/IOrder_Progress_State";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import OrderSourceEnum from "../../../../models/OrderSourceEnum";
import OrderStatesEnum from "../../../../models/OrderStatesEnum";
import OrderEntryService from "../../../../services/OrderEntryService";
import OrderProgressTracker from "../../../shared/OrderProgressTracker/OrderProgressTracker";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import OrderProgressStateGuide from "../OrderProgressStateGuide/OrderProgressStateGuide";
import "./OrderWorkflowConfigurator.scss";
import { OwWorkflowStateItem } from "./OwWorkflowStateItem/OwWorkflowStateItem";
import IWorkflow from "../../../../models/IWorkflow";

type Props = {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    updateFn: (e: any, subProp?: string) => void;
    workflow?: IWorkflow;
};

type State = {
    selectedItems: IOrder_Progress_State[];
    finalOption: IOrder_Progress_State[];
    availableItems: IOrder_Progress_State[];
    fakeOrder: Partial<IOrder>;
    fakeSupplier: ISupplierModel;
    fakeUser: IUserUI;
    itemEmails: { [key: number]: string };
    pdfPackingListEmails: { [key: number]: string };
    showHiddenFeatures: boolean;
};

class OrderWorkflowConfigurator extends Component<Props, State> {
    constructor(props: any) {
        super(props);

        this.state = this.setupState();
    }

    componentDidUpdate(prevProps: any, prevState: any, snapshot: any) {
        if (!this.state.showHiddenFeatures) {
            // @ts-expect-error
            if (!_.isNil(window.showHiddenFeatures)) {
                this.setState({
                    showHiddenFeatures: true,
                });
            }
        }
    }

    setupState = (): any => {
        return {
            selectedItems: this.getSelectedItems(),
            finalOption: this.getFinalOption(),
            availableItems: this.allConfigOptions(),
            fakeOrder: { order_source_id: OrderSourceEnum.ORDERWERKS, order_state_id: OrderStatesEnum.PRELIMINARY } as Partial<IOrder>,
            fakeSupplier: { ...this.props.supplierModel, order_progress_states: this.getSelectedItems() },
            fakeUser: { ...this.props.userModel },
            itemEmails: {},
            pdfPackingListEmails: {},
            showHiddenFeatures: false,
        };
    };

    getSelectedItems = (): IOrder_Progress_State[] => {
        if (this.props.workflow) {
            return this.props.workflow.detail;
        } else {
            if (this.props.supplierModel.order_progress_states) {
                return this.props.supplierModel.order_progress_states.filter((stateItem) => !stateItem.isFinalStep);
            } else {
                return OrderEntryService.getDefaultOrderSteps(this.props.supplierModel, OrderStatesEnum.PRELIMINARY);
            }
        }
    };

    getFinalOption = () => {
        if (this.props.workflow) {
            return this.props.workflow.detail.filter((item) => item.isFinalStep);
        } else {
            if (this.props.supplierModel.order_progress_states) {
                return this.props.supplierModel.order_progress_states.filter((stateItem) => stateItem.isFinalStep);
            } else {
                return [];
            }
        }
    };

    allConfigOptions = () => {
        const selectedItems = this.getSelectedItems();
        const finalOption = this.getFinalOption();

        const options: IOrder_Progress_State[] = [
            {
                order_state_id: OrderStatesEnum.PRELIMINARY,
                name: "preliminary",
                step_no: 1,
            },
            {
                order_state_id: OrderStatesEnum.SUBMITTED,
                name: "submitted",
                step_no: 2,
            },
            {
                order_state_id: OrderStatesEnum.IN_REVIEW,
                name: "in_review",
                step_no: 3,
            },
            {
                order_state_id: OrderStatesEnum.SCHEDULED,
                name: "scheduled",
                step_no: 3,
            },
            {
                order_state_id: OrderStatesEnum.CONFIRMED,
                name: "confirmed",
                step_no: 3,
            },
            {
                order_state_id: OrderStatesEnum.COMPLETED,
                name: "completed",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.VENDOR_APPROVAL_PAYMENT,
                name: "vendor_approval_payment",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.ACCOUNTING,
                name: "accounting",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.PAYMENT_RECEIVED_ORDER_BEING_PACKED,
                name: "payment_received_order_being_packed",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.PACKING_COMPLETED_AWAITING_SHIPPING_INFO,
                name: "packing_completed_awaiting_shipping_info",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.DEPOSIT_RECEIVED_ORDER_PACKING,
                name: "deposit_received_order_packing",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.PACKING_COMPLETED_AWAITING_PAYMENT_BALANCE_TO_SHIP,
                name: "packing_completed_awaiting_payment_balance_to_ship",
                step_no: 4,
            },
            {
                order_state_id: OrderStatesEnum.PROCESSED,
                name: "processed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PRINTED,
                name: "printed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.MOUNTED,
                name: "mounted",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.LAMINATED,
                name: "laminated",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.IN_QUEUE,
                name: "in_queue",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.AWAITING_REVIEW,
                name: "awaiting_review",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.UNDER_REVIEW,
                name: "under_review",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.APPROVED,
                name: "approved",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.IN_PROGRESS_PACKING,
                name: "in_progress_packing",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.FINISHING,
                name: "finishing",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.SUBMITTED_TO_PRODUCTION,
                name: "submitted_to_production",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.WELD_COMPLETED,
                name: "weld_completed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.INITIAL_QC_COMPLETED,
                name: "initial_qc_completed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PAINT_COMPLETED,
                name: "paint_completed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.FINAL_QC_COMPLETED,
                name: "final_qc_completed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PENDING,
                name: "pending",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.ACTIVE,
                name: "active",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.IN_DESIGN,
                name: "in_design",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.SENT_TO_PHARMACY,
                name: "sent_to_pharmacy",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.READY_FOR_PICK,
                name: "ready_for_pick",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PRE_PRESS,
                name: "pre_press",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.READY_TO_PRINT,
                name: "ready_to_print",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PRINTING,
                name: "printing",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.MATERIAL_CUTTING,
                name: "material_cutting",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.SEWING,
                name: "sewing",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.NEW,
                name: "new",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.WAREHOUSE,
                name: "warehouse",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.BILLED,
                name: "billed",
                step_no: 5,
            },
            {
                order_state_id: OrderStatesEnum.PARTS_ORDERED,
                name: "parts_ordered",
                step_no: 5,
            },
        ];

        const filteredItems: IOrder_Progress_State[] = [];

        for (const option of options) {
            let found = false;

            for (const selectedItem of selectedItems) {
                if (selectedItem.order_state_id === option.order_state_id) {
                    found = true;
                }
            }

            for (const finalItem of finalOption) {
                if (finalItem.order_state_id === option.order_state_id) {
                    found = true;
                }
            }

            if (!found) {
                filteredItems.push(option);
            }
        }

        return filteredItems;
    };

    reorder = (list: IOrder_Progress_State[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    move = (
        source: IOrder_Progress_State[],
        destination: IOrder_Progress_State[],
        droppableSource: DraggableLocation,
        droppableDestination: DraggableLocation
    ) => {
        const sourceClone = Array.from(source);
        const destClone = Array.from(destination);
        const [removed] = sourceClone.splice(droppableSource.index, 1);

        let index = droppableDestination.index;

        if (destClone.length && destClone[index].order_state_id === OrderStatesEnum.PRELIMINARY) {
            index += 1;
        }

        if (destClone.length) {
            destClone.splice(index, 0, removed);
        } else {
            destClone.push(removed);
        }

        const result: any = {
            sourceItems: sourceClone,
            destItems: destClone,
        };
        // result[droppableSource.droppableId] = sourceClone;
        // result[droppableDestination.droppableId] = destClone;

        return result;
    };

    onDragEnd = (result: DropResult, provided: ResponderProvided) => {
        const { source, destination } = result;

        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            let listId = "selectedItems";

            if (source.droppableId === "availableItems") {
                listId = "availableItems";
            } else if (source.droppableId === "finalOption") {
                listId = "finalOption";
            }

            // @ts-expect-error
            const list = this.state[listId];

            // We don't allow anything other than Preliminary to be in the first spot
            if (destination.index === 0) {
                return;
            }

            const items = this.reorder(list, source.index, destination.index);

            console.log("items: ", items);

            let step_no = 2;

            for (const item of items) {
                if (item.order_state_id !== OrderStatesEnum.PRELIMINARY) {
                    item.step_no = step_no;
                    step_no++;
                } else {
                    item.step_no = 1;
                }
            }

            if (source.droppableId === "selectedItems") {
                this.setState({ selectedItems: items }, () => {
                    this.pushUpdate();
                });
            } else if (source.droppableId === "availableItems") {
                this.setState({ availableItems: items }, () => {
                    this.pushUpdate();
                });
            } else if (source.droppableId === "finalOption") {
                this.setState({ finalOption: items }, () => {
                    this.pushUpdate();
                });
            }
        } else {
            console.log("source: ", source, "destination: ", destination);

            // @ts-expect-error
            const sourceItems: IOrder_Progress_State[] = this.state[source.droppableId];
            // @ts-expect-error
            const destinationItems: IOrder_Progress_State[] = this.state[destination.droppableId];

            const result = this.move(sourceItems, destinationItems, source, destination);

            console.log("result: ", result);

            let step_no = 1;

            for (const item of sourceItems) {
                item.step_no = step_no;
                step_no++;
            }

            let destination_step_no = 1;

            for (const item of destinationItems) {
                item.step_no = destination_step_no;
                destination_step_no++;
            }

            // console.log("sourceItems: ", sourceItems);
            // console.log("destinationItems: ", destinationItems);

            const state: any = {
                [source.droppableId]: result.sourceItems,
                [destination.droppableId]: result.destItems,
            };

            if (destination.droppableId === "finalOption" && state.finalOption.length) {
                state.finalOption[0].isFinalStep = true;
            }

            // console.log('newState', state);

            this.setState(state, () => {
                const fakeSupplier = this.state.fakeSupplier;

                fakeSupplier.order_progress_states = this.state.selectedItems;

                this.setState(
                    {
                        fakeSupplier,
                    },
                    () => {
                        this.pushUpdate();
                    }
                );
            });
        }
    };

    updateOptionConfig = (e: ChangeEvent<any>, item: IOrder_Progress_State, items?: "finalOption") => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        let itemsProp = "selectedItems";

        if (items === "finalOption") {
            itemsProp = "finalOption";
        }

        // @ts-expect-error
        const states = this.state[itemsProp];

        for (const state of states) {
            if (item.order_state_id === state.order_state_id) {
                // @ts-expect-error
                state[name] = value;
            }
        }

        const updatedState: any = {
            [itemsProp]: states,
        };

        this.setState(updatedState, () => {
            this.pushUpdate();
        });
    };

    pushUpdate = () => {
        let selectedItems = this.state.selectedItems;

        if (this.state.finalOption.length) {
            selectedItems = selectedItems.concat(this.state.finalOption);
        }

        const e = {
            target: {
                name: "order_progress_states",
                value: selectedItems,
            },
        };

        this.props.updateFn(e);
    };

    updateItemEmails = (e: string | string[], item: IOrder_Progress_State, items?: "finalOption") => {
        const emails = _.isArray(e) ? e.join(",") : e;

        const event: any = {
            target: {
                name: "email",
                value: emails,
            },
        };

        this.updateOptionConfig(event, item, items);
    };

    updateItemPdfEmails = (e: string | string[], item: IOrder_Progress_State, items?: "finalOption") => {
        const emails = _.isArray(e) ? e.join(",") : e;

        const event: any = {
            target: {
                name: "emailPDFPackingListTo",
                value: emails,
            },
        };

        console.log("updateItemEmails", event, item);

        this.updateOptionConfig(event, item, items);
    };

    handleChange = (e: any, item: IOrder_Progress_State) => {
        const itemEmails = this.state.itemEmails;

        if (!itemEmails[item.order_state_id]) {
            itemEmails[item.order_state_id] = "";
        }

        itemEmails[item.order_state_id] = e.target.value;

        this.setState({
            itemEmails,
        });
    };

    handlePdfEmailChange = (e: any, item: IOrder_Progress_State) => {
        const pdfPackingListEmails = this.state.pdfPackingListEmails;

        if (!pdfPackingListEmails[item.order_state_id]) {
            pdfPackingListEmails[item.order_state_id] = "";
        }

        pdfPackingListEmails[item.order_state_id] = e.target.value;

        this.setState({
            pdfPackingListEmails,
        });
    };

    addEmail = (item: IOrder_Progress_State, items?: "finalOption") => {
        const itemEmails = this.state.itemEmails;

        const email = itemEmails[item.order_state_id];

        const emails = item.email ? item.email.split(",") : [];

        emails.push(email);

        itemEmails[item.order_state_id] = "";

        let itemsProp = "selectedItems";

        if (items === "finalOption") {
            itemsProp = "finalOption";
        }

        // @ts-expect-error
        const selectedItems = this.state[itemsProp];

        for (const selectedItem of selectedItems) {
            if (Number(selectedItem.order_state_id) === Number(item.order_state_id)) {
                selectedItem.email = emails.join(",");
            }
        }

        const state: any = {
            [itemsProp]: selectedItems,
            itemEmails,
        };

        this.setState(state, () => {
            this.pushUpdate();
        });
    };

    addPdfEmail = (item: IOrder_Progress_State, items?: "finalOption") => {
        const pdfPackingListEmails = this.state.pdfPackingListEmails;

        const email = pdfPackingListEmails[item.order_state_id];

        const emails = item.emailPDFPackingListTo ? item.emailPDFPackingListTo.split(",") : [];

        emails.push(email);

        pdfPackingListEmails[item.order_state_id] = "";

        let itemsProp = "selectedItems";

        if (items === "finalOption") {
            itemsProp = "finalOption";
        }

        // @ts-expect-error
        const selectedItems = this.state[itemsProp];

        for (const selectedItem of selectedItems) {
            if (Number(selectedItem.order_state_id) === Number(item.order_state_id)) {
                selectedItem.emailPDFPackingListTo = emails.join(",");
            }
        }

        const state: any = {
            [itemsProp]: selectedItems,
            pdfPackingListEmails,
        };

        this.setState(state);
    };

    getSelectedWorkflowItem = (item: IOrder_Progress_State) => {};

    resetChanges = () => {
        const state = this.setupState();

        this.setState(state);
    };

    progressFakeOrder = () => {
        const fakeOrder = this.state.fakeOrder;
        const steps = this.state.selectedItems;

        let nextStep: IOrder_Progress_State | undefined;

        for (let i = 0; i < steps.length; ++i) {
            const step = steps[i];

            if (step.order_state_id === fakeOrder.order_state_id) {
                if (i + 1 === steps.length) {
                    nextStep = steps[0];
                } else {
                    nextStep = steps[i + 1];
                }

                continue;
            }
        }

        if (nextStep) {
            fakeOrder.order_state_id = nextStep.order_state_id;
        }

        this.setState({
            fakeOrder,
        });
    };

    isDragDisabled = (item: IOrder_Progress_State) => {
        switch (item.order_state_id) {
            case OrderStatesEnum.PRELIMINARY:
            case OrderStatesEnum.PARTIALLY_SHIPPED:
            case OrderStatesEnum.SHIPPED:
            case OrderStatesEnum.PICKED_UP:
            case OrderStatesEnum.READY_FOR_PICKUP:
            case OrderStatesEnum.DELIVERED:
            case OrderStatesEnum.READY_FOR_DELIVERY:
            case OrderStatesEnum.READY_TO_SHIP:
                return true;
                break;
        }
    };

    getOwWorkflowStateItemForOrderStateId = (orderStateId: OrderStatesEnum, options?: { hideHandle?: boolean }) => {
        const item = this.state.selectedItems.find((selectedItem) => selectedItem.order_state_id === orderStateId);

        if (item) {
            return (
                <OwWorkflowStateItem
                    item={item}
                    selectedItems={this.state.selectedItems}
                    updateOptionConfig={this.updateOptionConfig}
                    handlePdfEmailChange={this.handlePdfEmailChange}
                    pdfPackingListEmails={this.state.pdfPackingListEmails}
                    addEmail={this.addEmail}
                    addPdfEmail={this.addPdfEmail}
                    updateItemEmails={this.updateItemEmails}
                    updateItemPdfEmails={this.updateItemPdfEmails}
                    itemEmails={this.state.itemEmails}
                    handleChange={this.handleChange}
                    {...options}
                />
            );
        } else {
            return null;
        }
    };

    outputConfig = () => {
        console.log("OUTPUT CONFIG");
        console.log("Selected Items: ", this.state.selectedItems);
        console.log("Final Option: ", this.state.finalOption);
    };

    render() {
        return (
            <div className="OrderWorkflowConfigurator">
                {this.state.showHiddenFeatures ? (
                    <div>
                        <OwButton title={"output_config"} fn={this.outputConfig} />
                    </div>
                ) : null}
                <DragDropContext onDragEnd={this.onDragEnd}>
                    <OwTitle title={"selected_options"} tag={"h5"} />
                    {/*<OwButton className={'btn-danger mb-3'} leftIconClass={'fad fa-undo'} title={'reset_changes'} fn={this.resetChanges}/>*/}
                    <div className={"selectedOptionsWrapper"}>
                        <div className={"item selectedItem"}>
                            {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.PRELIMINARY, { hideHandle: true })}
                        </div>

                        <Droppable droppableId={"selectedItems"} direction={"horizontal"}>
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    className={
                                        "d-flex border border-secondary items-row " + (snapshot.isDraggingOver ? "dragging-over" : "")
                                    }>
                                    {this.state.selectedItems.map((item, index) => {
                                        const isDragDisabled = this.isDragDisabled(item);

                                        if (isDragDisabled) {
                                            return null;
                                        }

                                        return (
                                            <Draggable
                                                isDragDisabled={isDragDisabled}
                                                key={item.order_state_id}
                                                draggableId={item.name}
                                                index={index}>
                                                {(provided1, snapshot1, rubric) => (
                                                    <div
                                                        className={"item selectedItem"}
                                                        ref={provided1.innerRef}
                                                        {...provided1.draggableProps}
                                                        {...provided1.dragHandleProps}>
                                                        {this.getOwWorkflowStateItemForOrderStateId(item.order_state_id)}
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                    <br />
                    <OwTitle title={"available_options"} tag={"h5"} />
                    <Droppable droppableId={"availableItems"} direction={"horizontal"}>
                        {(provided, snapshot) => (
                            <div
                                className={"d-flex border border-secondary items-row " + (snapshot.isDraggingOver ? "dragging-over" : "")}
                                ref={provided.innerRef}>
                                {this.state.availableItems.map((item, index) => (
                                    <Draggable key={item.order_state_id} draggableId={item.name} index={index}>
                                        {(provided1, snapshot1, rubric) => (
                                            <div
                                                className={"item d-flex flex-column justify-content-start"}
                                                ref={provided1.innerRef}
                                                {...provided1.draggableProps}
                                                {...provided1.dragHandleProps}>
                                                <div className={"drag-handles"}>
                                                    <i className={"fas fa-grip-horizontal"}></i>
                                                </div>
                                                <OwLabel title={item.name} noBottomMargin={true} />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                    <div className={"d-flex align-items-end"}>
                        <OwTitle title={"final_option"} tag={"h5"} className={"me-2"} />
                        <OwTitle title={"optional"} tag={"h6"} isItalic={true} />
                    </div>

                    <Droppable droppableId={"finalOption"} direction={"horizontal"} isDropDisabled={!!this.state.finalOption.length}>
                        {(provided, snapshot) => (
                            <div
                                className={"d-flex border border-secondary items-row " + (snapshot.isDraggingOver ? "dragging-over" : "")}
                                ref={provided.innerRef}>
                                {this.state.finalOption?.map((item, index) => (
                                    <Draggable key={item.order_state_id} draggableId={item.name} index={index}>
                                        {(provided1, snapshot1, rubric) => (
                                            <div
                                                className={"item selectedItem"}
                                                ref={provided1.innerRef}
                                                {...provided1.draggableProps}
                                                {...provided1.dragHandleProps}>
                                                <OwWorkflowStateItem
                                                    item={item}
                                                    selectedItems={this.state.selectedItems}
                                                    updateOptionConfig={(e, _item) => {
                                                        this.updateOptionConfig(e, _item, "finalOption");
                                                    }}
                                                    handlePdfEmailChange={this.handlePdfEmailChange}
                                                    pdfPackingListEmails={this.state.pdfPackingListEmails}
                                                    addEmail={(_item) => {
                                                        this.addEmail(_item, "finalOption");
                                                    }}
                                                    addPdfEmail={(_item) => {
                                                        this.addPdfEmail(_item, "finalOption");
                                                    }}
                                                    updateItemEmails={(e, _item) => {
                                                        this.updateItemEmails(e, _item, "finalOption");
                                                    }}
                                                    updateItemPdfEmails={(e, _item) => {
                                                        this.updateItemPdfEmails(e, _item, "finalOption");
                                                    }}
                                                    itemEmails={this.state.itemEmails}
                                                    handleChange={this.handleChange}
                                                />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <br />
                <div className={"finalStates"}>
                    <OwTitle title={"fulfillment_options"} tag={"h5"} />
                    <div className={"selectedOptionsWrapper finalStateOptions"}>
                        <div className={"d-flex border border-secondary items-row"}>
                            {this.props.supplierModel.has_shipments ? (
                                <>
                                    {/*<div className={"item selectedItem"}>{this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.PARTIALLY_SHIPPED)}</div>*/}
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.READY_TO_SHIP, { hideHandle: true })}
                                    </div>
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.SHIPPED, { hideHandle: true })}
                                    </div>
                                </>
                            ) : null}
                            {this.props.supplierModel.has_local_delivery ? (
                                <>
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.READY_FOR_DELIVERY, {
                                            hideHandle: true,
                                        })}
                                    </div>
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.DELIVERED, { hideHandle: true })}
                                    </div>
                                </>
                            ) : null}
                            {this.props.supplierModel.has_local_pickup ? (
                                <>
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.READY_FOR_PICKUP, { hideHandle: true })}
                                    </div>
                                    <div className={"item selectedItem"}>
                                        {this.getOwWorkflowStateItemForOrderStateId(OrderStatesEnum.PICKED_UP, { hideHandle: true })}
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={"w-100 mt-5"}>
                    <OwTitle title={"validate_steps"} tag={"h5"} />
                    <div>
                        <OwButton title={"progress"} fn={this.progressFakeOrder} />
                    </div>
                    <OrderProgressTracker order={this.state.fakeOrder} orderStateId={this.state.fakeOrder.order_state_id!} />
                    <OrderProgressStateGuide
                        orderStates={this.state.selectedItems}
                        currentStateId={this.state.fakeOrder.order_state_id!}
                        supplierModel={this.props.supplierModel}
                    />
                </div>
            </div>
        );
    }
}

export default OrderWorkflowConfigurator;
